import { useMutation } from '@apollo/client'
import { parseCookies } from 'nookies'
import { SET_BILLING_ADDRESS_ON_CART } from '_app/graphql/mutations/cart/SetBillingAddressOnCart'
import { SET_GUEST_EMAIL_ON_CART } from '_app/graphql/mutations/cart/SetGuestEmailOnCart'
import { SET_SHIPPING_METHODS_ON_CART } from '_app/graphql/mutations/cart/SetShippingMethodsOnCart'
import {
  useSetShippingAddressOnCartMutation,
  CartAddressInput,
  AvailableShippingMethod,
} from '_app/types/api'
import useCart from './useCart'
import CookiesHelper from '../../helpers/CookiesHelper/CookiesHelper'

interface ShippingMethodOptions
  extends Partial<
    Pick<AvailableShippingMethod, 'paczkomaty_target_point' | 'showroom_target'>
  > {
  carrier_code: string;
}

export const useUpdateUserCart = () => {
  const { data } = useCart()
  const cartId = CookiesHelper.get('cartId')
  const [billingAddressMutation] = useMutation(SET_BILLING_ADDRESS_ON_CART)
  const [shippingAddressMutation] = useSetShippingAddressOnCartMutation()
  const [guestEmailMutation] = useMutation(SET_GUEST_EMAIL_ON_CART)
  const [shippingMethodMutation] = useMutation(SET_SHIPPING_METHODS_ON_CART)

  const setBillingAddressOnCart = (address: CartAddressInput) => {
    return billingAddressMutation({
      variables: {
        billingAddress: {
          address,
        },
      },
    })
  }

  const setShippingAddressOnCart = (address: CartAddressInput) => {
    return shippingAddressMutation({
      variables: {
        cartId,
        shippingAddresses: [{ address }],
      },
    })
  }

  const setGuestEmailOnCart = (email: string) => {
    return guestEmailMutation({
      variables: {
        email,
      },
    })
  }

  const setShippingMethodsOnCart = async ({
    carrier_code,
    showroom_target,
    paczkomaty_target_point,
  }: ShippingMethodOptions) => {
    const availableMethods =
      data?.cart?.shipping_addresses?.[0]?.available_shipping_methods || []

    return shippingMethodMutation({
      variables: {
        shippingMethods: [
          {
            carrier_code,
            method_code:
              availableMethods.find(
                method => method?.carrier_code === carrier_code
              )?.method_code || '',
            showroom_target,
            paczkomaty_target_point,
          },
        ],
      },
    })
  }

  return {
    setBillingAddressOnCart,
    setShippingAddressOnCart,
    setGuestEmailOnCart,
    setShippingMethodsOnCart,
  }
}
